// Extracted from src/shared/constants.js — canonical status mappings for GHL appointments

/**
 * Version identifier for the published privacy policy. Bump whenever the
 * guest-facing privacy policy materially changes so consent records capture
 * which version a guest accepted. See issue #175.
 */
export const PRIVACY_POLICY_VERSION = '2026-09-03';

/**
 * Default public URL for the guest-facing privacy policy.
 * #403 fix: this pointed at `/privacy`, a route the SPA never defined — the
 * consent checkbox linked to an empty app shell. The policy is the static
 * `public/privacy-policy.html`, served at this exact path.
 */
export const DEFAULT_PRIVACY_POLICY_URL = 'https://app.yeboguest.com/privacy-policy.html';

export const APPOINTMENT_STATUSES = {
  pending:   { label: 'Pending',      color: 'yellow' },
  confirmed: { label: 'Reserved',     color: 'blue' },
  invalid:   { label: 'Seated',       color: 'green' },
  showed:    { label: 'Checked Out',  color: 'gray' },
  cancelled: { label: 'Removed',      color: 'red' },
  'no-show': { label: 'Removed',      color: 'red' },
} as const;

export const MEAL_PERIODS = ['breakfast', 'lunch', 'dinner'] as const;
export type MealPeriod = (typeof MEAL_PERIODS)[number];

export interface MealTimeRange {
  start: number; // hour (24h)
  end: number;   // hour (24h)
}

export type MealTimes = Record<MealPeriod, MealTimeRange>;

export const DEFAULT_MEAL_TIMES: MealTimes = {
  breakfast: { start: 8, end: 11 },
  lunch:     { start: 11, end: 16 },
  dinner:    { start: 16, end: 22 },
};

export const DEFAULT_SLOT_SIZE_MIN = 15;
export const DEFAULT_BOOKING_DURATION_MINS = 90;
export const DEFAULT_TIMEZONE = 'Africa/Johannesburg';
export const DEFAULT_PRIMARY_COLOR = '#2563eb';
export const DEFAULT_FONT_FAMILY = 'system-ui';
export const DEFAULT_BORDER_RADIUS = '8px';
export const DEFAULT_PAYMENT_CURRENCY = 'zar';

// Widget chrome colours — match packages/widget/src/styles/tokens.css.ts
// hardcoded defaults so an owner who never sets these gets the widget's
// existing dark-warm look, unchanged.
export const DEFAULT_BACKGROUND_COLOR = '#1a1a1e';
export const DEFAULT_SIDEBAR_BACKGROUND_COLOR = '#c8b898';
export const DEFAULT_SIDEBAR_TEXT_COLOR = '#2a2420';
export const DEFAULT_TEXT_COLOR = '#e8dcc8';
export const DEFAULT_TEXT_MUTED_COLOR = '#b0a294';
export const DEFAULT_BORDER_COLOR = '#5a5a5e';

// ── Working Hours Helpers ────────────────────────────────────────────────────

import type {
  WorkingHoursSchedule,
  DepositOverride,
  DepositConfig,
  DayOfWeek,
  DailyMealsConfig,
  MealPeriodConfig,
  WeeklyMealsConfig,
} from './types.js';

export const DAY_OF_WEEK_ORDER: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function toHourDecimal(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const v = value.trim();
    if (TIME_RE.test(v)) {
      const [, hh, mm] = TIME_RE.exec(v)!;
      return Number(hh) + Number(mm) / 60;
    }
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }

  return fallback;
}

function dateToDayOfWeek(date: string): DayOfWeek | null {
  if (!date || typeof date !== 'string') return null;
  const dt = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(dt.getTime())) return null;
  const day = dt.getUTCDay();
  switch (day) {
    case 0: return 'sunday';
    case 1: return 'monday';
    case 2: return 'tuesday';
    case 3: return 'wednesday';
    case 4: return 'thursday';
    case 5: return 'friday';
    case 6: return 'saturday';
    default: return null;
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** HH:mm form of DEFAULT_MEAL_TIMES, used when a meal window has no usable start/end. */
function defaultTimeString(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * A start/end is kept VERBATIM when it is usable (finite number or non-empty
 * string) — `toHourDecimal` already accepts both forms, and coercing to HH:mm
 * would clamp legacy hour numbers like `end: 24` (midnight) down to 23:00.
 */
function normalizeMealTimeValue(value: unknown, fallback: string): number | string {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  return fallback;
}

function normalizeMealConfig(raw: unknown, period: MealPeriod): MealPeriodConfig {
  const src = isPlainRecord(raw) ? raw : {};
  const fallback = DEFAULT_MEAL_TIMES[period];
  return {
    // Fail CLOSED: a period we cannot read as explicitly enabled is treated as
    // closed rather than inventing opening hours for the venue.
    enabled: Boolean(src.enabled),
    start: normalizeMealTimeValue(src.start, defaultTimeString(fallback.start)),
    end: normalizeMealTimeValue(src.end, defaultTimeString(fallback.end)),
  };
}

/**
 * Make a meals map TOTAL — all three periods present. A period the raw map does
 * not carry is closed (`enabled: false`), so downstream readers never have to
 * guess (and never dereference an absent period).
 */
function normalizeMeals(raw: unknown): DailyMealsConfig {
  const src = isPlainRecord(raw) ? raw : {};
  return {
    breakfast: normalizeMealConfig(src.breakfast, 'breakfast'),
    lunch: normalizeMealConfig(src.lunch, 'lunch'),
    dinner: normalizeMealConfig(src.dinner, 'dinner'),
  };
}

/**
 * Normalize a per-weekday override map. A day the map NAMES is authoritative
 * (its absent periods are closed); a day it does not name inherits the
 * schedule-level meals — which is exactly what resolveMealsForScheduleDay does
 * when a weekday key is missing, so filling the type out here is behaviour-neutral.
 */
function normalizeDayRules(raw: unknown, baseMeals: DailyMealsConfig): WeeklyMealsConfig {
  const src = isPlainRecord(raw) ? raw : {};
  const out = {} as WeeklyMealsConfig;
  for (const day of DAY_OF_WEEK_ORDER) {
    out[day] = day in src ? normalizeMeals(src[day]) : baseMeals;
  }
  return out;
}

function resolveMealsForScheduleDay(
  schedule: WorkingHoursSchedule,
  date?: string,
): DailyMealsConfig {
  const dayRules: unknown = schedule?.dayRules;
  if (date && isPlainRecord(dayRules)) {
    const dayKey = dateToDayOfWeek(date);
    const dayEntry = dayKey ? dayRules[dayKey] : undefined;
    if (dayEntry) return normalizeMeals(dayEntry);
  }
  return normalizeMeals(schedule?.meals);
}

function coerceScheduleString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** A schedule is only addressable by date if both bounds are usable date strings. */
function usableDateBound(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return v === '' ? null : v;
}

/**
 * Normalize a raw `workingHoursSchedules` value (issue #485). Prod LocationConfig
 * items can carry a LEGACY element shaped `{isDefault,label,rules}` — no
 * startDate/endDate/meals — which findScheduleForDate can never match and which
 * used to crash scheduleMealTimes on `meals[period].enabled`.
 *
 * Contract: total and NEVER throws (this feeds a public, unauthenticated
 * endpoint). Non-array input → `[]`. Elements without BOTH bounds usable are
 * DROPPED rather than repaired — an undated schedule has no date range to apply
 * to, so keeping it could only ever shadow a real one or resolve to nothing.
 * Every survivor gets a total meals map. Read-side only: nothing is written back.
 */
export function normalizeWorkingHoursSchedules(raw: unknown): WorkingHoursSchedule[] {
  if (!Array.isArray(raw)) return [];

  const out: WorkingHoursSchedule[] = [];
  raw.forEach((element, index) => {
    if (!isPlainRecord(element)) return;

    const startDate = usableDateBound(element.startDate);
    const endDate = usableDateBound(element.endDate);
    if (!startDate || !endDate) return;

    const meals = normalizeMeals(element.meals);
    const schedule: WorkingHoursSchedule = {
      // Positional fallback id — stable across reads of the same item, unlike a
      // random one, so widget/dashboard keys don't churn between requests.
      id: coerceScheduleString(element.id) || `whs_${index}`,
      label: coerceScheduleString(element.label),
      startDate,
      endDate,
      meals,
    };
    if (isPlainRecord(element.dayRules)) {
      schedule.dayRules = normalizeDayRules(element.dayRules, meals);
    }
    out.push(schedule);
  });

  return out;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** #1048 — a schedule's length in days; unparseable dates sort last (never win). */
function scheduleSpanDays(s: WorkingHoursSchedule): number {
  const start = Date.parse(`${s.startDate}T00:00:00Z`);
  const end = Date.parse(`${s.endDate}T00:00:00Z`);
  const span = (end - start) / DAY_MS;
  return Number.isFinite(span) ? span : Number.POSITIVE_INFINITY;
}

/**
 * Find the working hours schedule that covers a given date (YYYY-MM-DD), or
 * undefined if none does.
 *
 * #1048 — when several schedules cover the date, the MOST SPECIFIC one wins:
 * the shortest date range, ties broken by list order. A festive or seasonal
 * override is narrower than the year-round schedule it sits on, and venues
 * cannot reorder schedules, so "first in the list" silently kept year-round
 * hours on festive dates. Matching is unchanged: undated legacy elements never
 * match (#485), and a single match is returned exactly as before.
 */
export function findScheduleForDate(
  schedules: WorkingHoursSchedule[],
  date: string,
): WorkingHoursSchedule | undefined {
  let best: WorkingHoursSchedule | undefined;
  let bestSpan = Number.POSITIVE_INFINITY;
  for (const s of schedules) {
    if (!(date >= s.startDate && date <= s.endDate)) continue;
    const span = scheduleSpanDays(s);
    // Strictly shorter only, so an equal span keeps the earlier schedule.
    if (best === undefined || span < bestSpan) {
      best = s;
      bestSpan = span;
    }
  }
  return best;
}

/**
 * Build a MealTimes object from a schedule (only including enabled meals).
 * Returns null if no meals are enabled for that schedule.
 */
export function scheduleMealTimes(schedule: WorkingHoursSchedule, date?: string): MealTimes | null {
  const meals = resolveMealsForScheduleDay(schedule, date);
  const result: Partial<MealTimes> = {};
  let anyEnabled = false;
  for (const period of MEAL_PERIODS) {
    const cfg = meals[period];
    if (cfg.enabled) {
      const fallback = DEFAULT_MEAL_TIMES[period];
      result[period] = {
        start: toHourDecimal(cfg.start, fallback.start),
        end: toHourDecimal(cfg.end, fallback.end),
      };
      anyEnabled = true;
    }
  }
  return anyEnabled ? (result as MealTimes) : null;
}

/**
 * Resolve the per-person deposit amount for a given date.
 * Returns the override amount if one matches, otherwise the default.
 */
export function resolveDepositForDate(
  config: DepositConfig,
  date: string,
): number {
  for (const override of config.overrides) {
    if (override.dates.includes(date)) {
      return override.amountPerPerson;
    }
  }
  return config.defaultAmountPerPerson;
}

/**
 * Resolve the per-kid deposit amount for a given date.
 * Returns the override kids amount if one matches, otherwise the default kids amount (0 if unset).
 */
export function resolveKidsDepositForDate(
  config: DepositConfig,
  date: string,
): number {
  for (const override of config.overrides) {
    if (override.dates.includes(date)) {
      return override.kidsAmountPerPerson ?? 0;
    }
  }
  return config.defaultKidsAmountPerPerson ?? 0;
}

// Status normalization (server enum → canonical)
const SERVER_TO_CANONICAL = new Map<string, string>([
  ['new',         'pending'],
  ['unconfirmed', 'pending'],
  ['confirmed',   'confirmed'],
  ['invalid',     'invalid'],
  ['showed',      'showed'],
  ['cancelled',   'cancelled'],
  ['no-show',     'no-show'],
]);

export function normalizeToCanonical(status: string | null | undefined): string {
  const raw = String(status || '').toLowerCase().trim();
  return SERVER_TO_CANONICAL.get(raw) || 'pending';
}

export function mapCanonicalToServer(canon: string | null | undefined): string {
  const c = String(canon || '').toLowerCase().trim();
  switch (c) {
    case 'pending':   return 'new';
    case 'confirmed': return 'confirmed';
    case 'invalid':   return 'invalid';
    case 'showed':    return 'showed';
    case 'cancelled': return 'cancelled';
    case 'no-show':   return 'no-show';
    default:          return c;
  }
}
